import { formatNaira } from "@/lib/formatters";

export interface PriceDraft {
  basePrice: string;
  perPagePrice: string;
  perCopyPrice: string;
  colorPagePrice: string;
}

export const EMPTY_PRICE_DRAFT: PriceDraft = {
  basePrice: "0",
  perPagePrice: "0",
  perCopyPrice: "0",
  colorPagePrice: "0",
};

export const PRICE_FIELDS: {
  key: keyof PriceDraft;
  label: string;
  hint: string;
}[] = [
  {
    key: "basePrice",
    label: "Binding fee",
    hint: "Flat amount for every copy, before pages are counted.",
  },
  {
    key: "perPagePrice",
    label: "Price per page",
    hint: "Charged for every page of every copy.",
  },
  {
    key: "colorPagePrice",
    label: "Color page surcharge",
    hint: "Added on top of the page rate, only for pages printed in color.",
  },
  {
    key: "perCopyPrice",
    label: "Extra fee per copy",
    hint: "Added once for every copy, on top of the binding fee. Leave at 0 if there is no extra fee.",
  },
];

export const SAMPLE_THESIS = { pages: 100, colorPages: 4, copies: 2 };

export interface QuoteLine {
  label: string;
  detail: string;
  amount: number;
}

function amount(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function quoteSample(draft: PriceDraft): { lines: QuoteLine[]; total: number } {
  const binding = amount(draft.basePrice);
  const page = amount(draft.perPagePrice);
  const color = amount(draft.colorPagePrice);
  const extra = amount(draft.perCopyPrice);
  const { pages, colorPages, copies } = SAMPLE_THESIS;

  const lines: QuoteLine[] = [
    {
      label: "Binding fee",
      detail: `${formatNaira(binding)} × ${copies} copies`,
      amount: binding * copies,
    },
    {
      label: "Pages",
      detail: `${pages} × ${formatNaira(page)} × ${copies} copies`,
      amount: pages * page * copies,
    },
    {
      label: "Color pages",
      detail: `${colorPages} × ${formatNaira(color)} × ${copies} copies`,
      amount: colorPages * color * copies,
    },
    {
      label: "Extra fee",
      detail: `${formatNaira(extra)} × ${copies} copies`,
      amount: extra * copies,
    },
  ];

  return { lines, total: lines.reduce((sum, line) => sum + line.amount, 0) };
}
